import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddManualEventModal({ courseId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    event_type: 'assignment',
    title: '',
    description: '',
    due_date: ''
  });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    { value: 'assignment', label: 'Assignment' },
    { value: 'exam', label: 'Exam' },
    { value: 'project', label: 'Project' },
    { value: 'quiz', label: 'Quiz' },
    { value: 'reading', label: 'Reading' },
    { value: 'study_session', label: 'Study Session' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/study-buddy/courses/${courseId}/manual-events`,
        null,
        {
          params: formData,
          withCredentials: true
        }
      );
      
      toast.success('Event added successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding event:', error);
      toast.error(error.response?.data?.detail || 'Failed to add event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#F4E4C1] to-white rounded-lg max-w-lg w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-[#E85D4D]"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-black mb-6">Add Manual Event</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Event Type *
            </label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
              required
            >
              {eventTypes.map(type => (
                <option key={type.value} value={type.value} className="text-black">{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
              placeholder="e.g., Chapter 5 Quiz"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Due Date *
            </label>
            <input
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] h-24 resize-none text-black bg-white"
              placeholder="Add any additional details..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-[#E8B67A] text-black font-bold rounded-lg hover:bg-[#E8B67A]/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
